import React from "react";
import { AsInput, KycSelect, KycDateTimePicker, KycRadioGroup } from "../../../kyc";
import { IS_WEB, kycCountrySelectOptionList } from "@adgm/libs";
import type { IColumnConfig } from "@adgm/cross-models";
import { fontFamily, fontSize } from "../../../styles/common";
import { Text, View } from "../../../platformComponents";

/**
 * 表单字段渲染器组件
 * @description 根据字段类型渲染相应的输入组件，支持多种字段类型
 */
interface IFormFieldRendererProps {
  /** 字段配置 */
  column: IColumnConfig;
  /** 父组件IRowData为任意键值对，组件为公共渲染组件。所以这里使用any便于处理 */
  value: any;
  error?: string;
  /** 字段值更新回调 */
  onValueChange: (value: any) => void;
  onValidation?: (column: IColumnConfig, value: any) => void;
}

export const FormFieldRenderer: React.FC<IFormFieldRendererProps> = ({
  column,
  value,
  error,
  onValueChange,
  onValidation,
}) => {
  const handleValueChange = (newValue: any) => {
    onValueChange(newValue);
    onValidation?.(column, newValue);
  };

  const renderLabel = () => {
    return (
      <Text
        style={{
          fontSize: IS_WEB ? fontSize.f14 : fontSize.f12,
          fontFamily: IS_WEB ? "Common-Regular" : fontFamily.commonRegular,
        }}>
        {column.title}
      </Text>
    );
  };

  switch (column.type) {
    case "percentage":
      return (
        <AsInput
          required={column.required}
          key={column.key}
          label={renderLabel()}
          value={value || ""}
          error={error}
          valueType="number"
          precision={2}
          onValueChange={handleValueChange}
        />
      );

    case "nationality":
      return (
        <KycSelect
          required={column.required}
          className="commonSelect"
          value={value || undefined}
          label={renderLabel()}
          error={error}
          options={kycCountrySelectOptionList("commonName")}
          onValueChange={handleValueChange}
          multiple
        />
      );

    case "number":
      return (
        <AsInput
          required={column.required}
          key={column.key}
          label={renderLabel()}
          value={value || ""}
          error={error}
          valueType="number"
          precision={2}
          onValueChange={handleValueChange}
        />
      );

    case "date":
      return (
        <KycDateTimePicker
          required={column.required}
          key={column.key}
          label={renderLabel()}
          value={value || ""}
          error={error}
          dateFormat={column.dateFormat || "YYYY-MM-DD"}
          onValueChange={handleValueChange}
        />
      );

    case "select":
      return (
        <KycSelect
          required={column.required}
          key={column.key}
          className="commonSelect"
          value={value || undefined}
          label={renderLabel()}
          error={error}
          options={column.options || []}
          onValueChange={handleValueChange}
        />
      );

    case "radioWithInput":
    case "radio":
      return (
        <KycRadioGroup
          required={column.required}
          key={column.key}
          label={renderLabel()}
          value={value || undefined}
          error={error}
          options={column.options || []}
          onValueChange={handleValueChange}
          placeholder={column.placeholder}
        />
      );

    case "amount":
      return (
        <View key={column.key} style={{ flexDirection: "row", alignItems: "center" }}>
          <AsInput
            required={column.required}
            label={renderLabel()}
            value={value || ""}
            error={error}
            valueType="number"
            precision={2}
            onValueChange={handleValueChange}
          />
        </View>
      );

    default:
      return (
        <AsInput
          required={column.required}
          key={column.key}
          label={renderLabel()}
          value={value || ""}
          error={error}
          onValueChange={handleValueChange}
          maxLength={500}
        />
      );
  }
};
